import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Save, Loader2, Bus, MapPin, Clock, Users, Car, Plus, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';

const ScheduleModal = ({ isOpen, onClose, onSave, schedule, vehicles, routes }) => {
  const [formData, setFormData] = useState({
    scheduleName: '',
    busID: '',
    routeID: '',
    basePrice: 0,
    description: '',
    // Fixed times for all dates
    departureTime: '',
    arrivalTime: '',
    availableSeats: 0
  });
  
  const [selectedDates, setSelectedDates] = useState([]);
  const selectedDatesRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const isEditing = !!schedule;

  useEffect(() => {
    if (schedule) {
      const vehicle = vehicles && Array.isArray(vehicles) ? vehicles.find(v => v && v.id === schedule.busID) : null;
      const route = routes && Array.isArray(routes) ? routes.find(r => r && r.routeID === schedule.routeID) : null;
      
      setSelectedVehicle(vehicle);
      setSelectedRoute(route);
      
      // Get totalSeats from vehicle (handle both camelCase and PascalCase)
      const vehicleTotalSeats = vehicle?.totalSeats || vehicle?.TotalSeats || 0;
      
      setFormData({
        scheduleName: schedule.scheduleName || '',
        busID: schedule.busID || '',
        routeID: schedule.routeID || '',
        basePrice: schedule.basePrice || 0,
        description: schedule.description || '',
        departureTime: '',
        arrivalTime: '',
        // Auto-load available seats from vehicle's totalSeats if vehicle exists
        availableSeats: vehicleTotalSeats
      });
      
      // Load existing schedule dates if editing
      if (schedule.scheduleDates && Array.isArray(schedule.scheduleDates)) {
        setSelectedDates(schedule.scheduleDates.map(sd => new Date(sd.date)));
        if (schedule.scheduleDates.length > 0) {
          setFormData(prev => ({
            ...prev,
            departureTime: schedule.scheduleDates[0].departureTime || '',
            arrivalTime: schedule.scheduleDates[0].arrivalTime || '',
            // Keep vehicle's totalSeats for availableSeats (don't override with schedule data)
            availableSeats: vehicle?.totalSeats || vehicle?.TotalSeats || prev.availableSeats || 0
          }));
        }
      }
    } else {
      setFormData({
        scheduleName: '',
        busID: '',
        routeID: '',
        basePrice: 0,
        description: '',
        departureTime: '',
        arrivalTime: '',
        availableSeats: 0
      });
      setSelectedVehicle(null);
      setSelectedRoute(null);
      setSelectedDates([]);
    }
    setErrors({});
  }, [schedule, vehicles, routes]);

  // Auto-update availableSeats when selectedVehicle changes
  useEffect(() => {
    if (selectedVehicle) {
      const totalSeats = selectedVehicle.totalSeats || selectedVehicle.TotalSeats || 0;
      if (totalSeats > 0) {
        console.log(`Auto-loading available seats from vehicle: ${totalSeats}`);
        setFormData(prev => ({
          ...prev,
          availableSeats: totalSeats
        }));
      } else {
        console.warn(`Vehicle ${selectedVehicle.registrationNumber || selectedVehicle.id} has no totalSeats property`);
      }
    }
  }, [selectedVehicle]);

  // Debug selectedDates changes and keep ref in sync
  useEffect(() => {
    console.log('=== SELECTEDDATES CHANGED ===');
    console.log('New selectedDates count:', selectedDates.length);
    console.log('New selectedDates:', selectedDates.map(d => d.toISOString().split('T')[0]));
    console.log('=== END SELECTEDDATES CHANGED ===');
    
    // Keep ref in sync
    selectedDatesRef.current = selectedDates;
  }, [selectedDates]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleVehicleChange = (e) => {
    const vehicleId = parseInt(e.target.value);
    const vehicle = vehicles && Array.isArray(vehicles) ? vehicles.find(v => v && v.id === vehicleId) : null;
    setSelectedVehicle(vehicle);
    setFormData(prev => ({ 
      ...prev, 
      busID: vehicleId,
      availableSeats: vehicle?.totalSeats || vehicle?.TotalSeats || 0
    }));
  };

  const handleRouteChange = (e) => {
    const routeId = parseInt(e.target.value);
    const route = routes && Array.isArray(routes) ? routes.find(r => r && r.routeID === routeId) : null;
    setSelectedRoute(route);
    setFormData(prev => ({ 
      ...prev, 
      routeID: routeId,
      basePrice: route?.basePrice || 0
    }));
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const toggleDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const isSelected = selectedDates.some(d => d.toISOString().split('T')[0] === dateStr);
    
    console.log('=== DATE SELECTION DEBUG ===');
    console.log('Clicked date:', dateStr);
    console.log('Clicked date object:', date);
    console.log('Currently selected dates count:', selectedDates.length);
    console.log('Currently selected dates:', selectedDates.map(d => d.toISOString().split('T')[0]));
    console.log('Is date already selected:', isSelected);
    
    // Check if the date is already in the array using a more robust comparison
    const isAlreadySelected = selectedDates.some(d => {
      const dStr = d.toISOString().split('T')[0];
      const isMatch = dStr === dateStr;
      console.log(`Comparing ${dStr} with ${dateStr}: ${isMatch}`);
      return isMatch;
    });
    
    console.log('Robust comparison result:', isAlreadySelected);
    
    if (isAlreadySelected) {
      const newDates = selectedDates.filter(d => d.toISOString().split('T')[0] !== dateStr);
      console.log('Removing date. New dates count:', newDates.length);
      console.log('Removing date. New dates:', newDates.map(d => d.toISOString().split('T')[0]));
      setSelectedDates(newDates);
      selectedDatesRef.current = newDates;
    } else {
      const newDates = [...selectedDates, date];
      console.log('Adding date. New dates count:', newDates.length);
      console.log('Adding date. New dates:', newDates.map(d => d.toISOString().split('T')[0]));
      console.log('Last date in new array:', newDates[newDates.length - 1]?.toISOString().split('T')[0]);
      setSelectedDates(newDates);
      selectedDatesRef.current = newDates;
    }
    console.log('=== END DATE SELECTION DEBUG ===');
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    const isSelected = selectedDates.some(d => d.toISOString().split('T')[0] === dateStr);
    console.log(`Checking if date ${dateStr} is selected:`, isSelected);
    return isSelected;
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.scheduleName.trim()) {
      newErrors.scheduleName = 'Schedule name is required';
    }

    if (!formData.busID) {
      newErrors.busID = 'Vehicle selection is required';
    }

    if (!formData.routeID) {
      newErrors.routeID = 'Route selection is required';
    }

    if (!isEditing && formData.basePrice <= 0) {
      newErrors.basePrice = 'Base price must be greater than 0';
    }

    if (!formData.departureTime) {
      newErrors.departureTime = 'Departure time is required';
    }

    if (!formData.arrivalTime) {
      newErrors.arrivalTime = 'Arrival time is required';
    }

    if (formData.departureTime && formData.arrivalTime) {
      const departure = new Date(`2000-01-01T${formData.departureTime}`);
      const arrival = new Date(`2000-01-01T${formData.arrivalTime}`);
      
      if (departure >= arrival) {
        newErrors.arrivalTime = 'Arrival time must be after departure time';
      }
    }

    if (selectedDates.length === 0) {
      newErrors.selectedDates = 'Please select at least one date';
    }

    if (formData.availableSeats <= 0) {
      newErrors.availableSeats = 'Available seats must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    
    try {
      // Get the most current selectedDates from ref
      const currentSelectedDates = selectedDatesRef.current;
      
      // Create schedule data
      const scheduleData = {
        ScheduleName: formData.scheduleName,
        BusID: parseInt(formData.busID),
        RouteID: parseInt(formData.routeID),
        BasePrice: parseFloat(formData.basePrice),
        Description: formData.description,
        // Create schedule dates for all selected dates
        ScheduleDates: currentSelectedDates.map(date => {
          // Create a date string in YYYY-MM-DD format to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          
          console.log(`Converting date: ${date.toDateString()} -> ${dateString}`);
          
          return {
            Date: dateString, // Send date string in YYYY-MM-DD format
            DepartureTime: formData.departureTime,
            ArrivalTime: formData.arrivalTime,
            Price: parseFloat(formData.basePrice),
            AvailableSeats: parseInt(formData.availableSeats)
          };
        })
      };

      // Debug logging
      console.log('=== SCHEDULE SAVE DEBUG ===');
      console.log('Current selected dates count:', currentSelectedDates.length);
      console.log('Current selected dates:', currentSelectedDates.map(d => d.toISOString().split('T')[0]));
      console.log('Current selected dates objects:', currentSelectedDates);
      console.log('Last selected date:', currentSelectedDates[currentSelectedDates.length - 1]?.toISOString().split('T')[0]);
      console.log('Schedule dates being sent count:', scheduleData.ScheduleDates.length);
      console.log('Schedule dates being sent:', JSON.stringify(scheduleData.ScheduleDates, null, 2));
      console.log('Full schedule data:', JSON.stringify(scheduleData, null, 2));
      console.log('=== END DEBUG ===');

      if (isEditing) {
        scheduleData.scheduleID = schedule.scheduleID;
        await onSave(scheduleData);
      } else {
        await onSave(scheduleData);
      }
      
      console.log('Schedule saved successfully');
    } catch (error) {
      console.error('Error saving schedule:', error);
      setApiError(error.message || 'Failed to save schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {isEditing ? 'Edit Schedule' : 'Add New Schedule'}
                  </h3>
                  <p className="text-blue-100">
                    {isEditing ? 'Update schedule information' : 'Create a new schedule with multiple dates'}
                  </p>
                  {loading && (
                    <div className="flex items-center justify-center space-x-2 text-blue-200 text-sm mt-1">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving schedule...</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* API Error Message */}
            {apiError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <strong>Error:</strong> {apiError}
                <button 
                  onClick={() => setApiError('')}
                  className="float-right font-bold text-red-700 hover:text-red-900"
                >
                  ×
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Schedule Info */}
              <div className="lg:col-span-1">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Schedule Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Schedule Name *
                    </label>
                    <input
                      type="text"
                      name="scheduleName"
                      value={formData.scheduleName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.scheduleName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Morning Express, Evening Service"
                      disabled={loading}
                    />
                    {errors.scheduleName && (
                      <p className="mt-1 text-sm text-red-600">{errors.scheduleName}</p>
                    )}
                  </div>

                  {/* Vehicle & Route Selection */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vehicle *
                      </label>
                      <select
                        name="busID"
                        value={formData.busID}
                        onChange={handleVehicleChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.busID ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      >
                        <option value="">Select Vehicle</option>
                        {vehicles && Array.isArray(vehicles) && vehicles.filter(v => v && v.isAvailable).map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.registrationNumber} - {vehicle.brand} {vehicle.model}
                          </option>
                        ))}
                      </select>
                      {errors.busID && (
                        <p className="mt-1 text-sm text-red-600">{errors.busID}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Route *
                      </label>
                      <select
                        name="routeID"
                        value={formData.routeID}
                        onChange={handleRouteChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.routeID ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      >
                        <option value="">Select Route</option>
                        {routes && Array.isArray(routes) && routes.filter(r => r && r.isActive).map(route => (
                          <option key={route.routeID} value={route.routeID}>
                            {route.fromLocation} → {route.toLocation}
                          </option>
                        ))}
                      </select>
                      {errors.routeID && (
                        <p className="mt-1 text-sm text-red-600">{errors.routeID}</p>
                      )}
                    </div>
                  </div>

                  {/* Base Price & Available Seats */}
                  <div className="space-y-4">
                    {!isEditing && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Base Price (Rs) *
                        </label>
                        <input
                          type="number"
                          name="basePrice"
                          value={formData.basePrice}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors.basePrice ? 'border-red-500' : 'border-gray-300'
                          }`}
                          disabled={loading}
                          readOnly={!!selectedRoute}
                        />
                        {selectedRoute && (
                          <p className="mt-1 text-sm text-blue-600">
                            Auto-loaded from route: {selectedRoute.fromLocation} → {selectedRoute.toLocation}
                          </p>
                        )}
                        {errors.basePrice && (
                          <p className="mt-1 text-sm text-red-600">{errors.basePrice}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Seats *
                      </label>
                      <input
                        type="number"
                        name="availableSeats"
                        value={formData.availableSeats}
                        onChange={handleInputChange}
                        min="1"
                                                 max={selectedVehicle?.totalSeats || 999}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.availableSeats ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                        readOnly={!!selectedVehicle}
                      />
                                             {selectedVehicle && (
                         <p className="mt-1 text-sm text-blue-600">
                           Auto-loaded from vehicle: {selectedVehicle.registrationNumber || selectedVehicle.RegistrationNumber || 'N/A'} (Max: {selectedVehicle.totalSeats || selectedVehicle.TotalSeats || 0})
                         </p>
                       )}
                      {errors.availableSeats && (
                        <p className="mt-1 text-sm text-red-600">{errors.availableSeats}</p>
                      )}
                    </div>
                  </div>

                  {/* Fixed Times */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Departure Time * (Same for all dates)
                      </label>
                      <input
                        type="time"
                        name="departureTime"
                        value={formData.departureTime}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.departureTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      />
                      {errors.departureTime && (
                        <p className="mt-1 text-sm text-red-600">{errors.departureTime}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Arrival Time * (Same for all dates)
                      </label>
                      <input
                        type="time"
                        name="arrivalTime"
                        value={formData.arrivalTime}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.arrivalTime ? 'border-red-500' : 'border-gray-300'
                        }`}
                        disabled={loading}
                      />
                      {errors.arrivalTime && (
                        <p className="mt-1 text-sm text-red-600">{errors.arrivalTime}</p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      maxLength={200}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Optional description for this schedule"
                      disabled={loading}
                    />
                    <div className="mt-1 text-right text-xs text-gray-500">
                      {formData.description.length}/200 characters
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full px-6 py-3 rounded-xl font-semibold text-white transition-all duration-200 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-lg hover:shadow-xl disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Save className="w-5 h-5" />
                          <span>{isEditing ? 'Update Schedule' : 'Create Schedule'}</span>
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Right Column - Calendar for Date Selection */}
              <div className="lg:col-span-1">
                <div className="space-y-6">
                  {/* Calendar Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">Select Dates</h4>
                    <div className="text-sm text-gray-600">
                      {selectedDates.length} date(s) selected
                    </div>
                  </div>

                  {/* Error for no dates selected */}
                  {errors.selectedDates && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      {errors.selectedDates}
                    </div>
                  )}

                  {/* Calendar Navigation */}
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => navigateMonth(-1)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h5 className="text-lg font-semibold text-gray-900">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h5>
                    <button
                      type="button"
                      onClick={() => navigateMonth(1)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 bg-gray-50">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="px-3 py-2 text-center text-sm font-medium text-gray-500">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Days */}
                    <div className="grid grid-cols-7">
                      {days.map((day, index) => (
                        <div
                          key={index}
                          className={`p-2 text-center text-sm border-r border-b border-gray-100 min-h-[40px] flex items-center justify-center ${
                            !day ? 'bg-gray-50' : ''
                          }`}
                        >
                          {day && (
                            <button
                              type="button"
                              onClick={() => toggleDate(day)}
                              disabled={isDateInPast(day)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                isDateSelected(day)
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : isDateInPast(day)
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              {day.getDate()}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Selected Dates Summary */}
                  {selectedDates.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-medium text-blue-900 mb-2">Selected Dates:</h5>
                      <div className="flex flex-wrap gap-2">
                        {selectedDates
                          .sort((a, b) => a - b)
                          .map((date, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {date.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              <button
                                type="button"
                                onClick={() => toggleDate(date)}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
